import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import {
  ContentSection,
  DataSectionItem,
} from '@app/components/blocks/contentSection/ContentSection'
import useMediaQuery from '@app/util/useMediaQuery'
import { ParagraphCollapsible } from '@app/components/blocks/contentCollapsible/ParagraphCollapsible'
import { ContentCollapsible } from '@app/components/blocks/contentCollapsible/ContentCollapsible'
import { ViewInitiativeModel } from 'functions/app/initiative/view'
import { addressBuilder } from '@app/util/helpers'
import { Title } from '@app/components/core/typography/Title'
import { ParagraphedText } from '@app/components/core/typography/ParagraphedText'

export const Overview = ({
  initiative,
}: {
  initiative: ViewInitiativeModel
}) => {
  const isMobile = useMediaQuery()
  const t = useTranslations().app
  const translations = t.initiativePage
  const numberOfHomesOptions =
    t.dashboard.initiative.creation.numberOfHomesOptions
  const notSpecified = t.common.notSpecified
  const geo = initiative.location.geo
  const address =
    addressBuilder(initiative.location.address) ?? translations.unknownLocation

  const renderMap = !!geo.lat && !!geo.long
  const renderSearchingDescription =
    initiative.searchingForMembers && initiative.searchingForMembersDescription

  const pageContentBlocks = initiative.contentBlocks

  const contactInformationListItems: DataSectionItem[] = [
    {
      label: translations.contactInformationKeys.contactPerson,
      content: initiative.contactFirstName
        ? `${initiative.contactFirstName} ${initiative.contactLastName}`
        : notSpecified,
      styling: 'text',
    },
  ]

  const featuresListItems: DataSectionItem[] = [
    {
      label: translations.overviewFeaturesKeys.location,
      content: address,
      styling: 'text',
    },
    {
      label: translations.overviewFeaturesKeys.phase,
      content: initiative.phase?.name ?? notSpecified,
      styling: 'text',
    },
    {
      label: translations.overviewFeaturesKeys.developmentForm,
      content: initiative.developmentForm?.name ?? notSpecified,
      styling: 'text',
    },
    {
      label: translations.overviewFeaturesKeys.numberOfHouseholds,
      content: initiative.numberOfHomes
        ? numberOfHomesOptions[initiative.numberOfHomes]
        : notSpecified,
      styling: 'text',
    },
    {
      label: translations.overviewFeaturesKeys.livingEnvironment,
      content:
        initiative.livingEnvironments?.map((le) => le.name).join(', ') ||
        notSpecified,
      styling: 'text',
    },
    {
      label: translations.overviewFeaturesKeys.targetAudience,
      content:
        initiative.targetAudiences?.map((ta) => ta.name).join(', ') ||
        notSpecified,
      styling: 'multi-line-text',
    },
    {
      label: translations.overviewFeaturesKeys.tags,
      content:
        initiative.interests?.map((interest) => interest.name).join(', ') ||
        notSpecified,
      styling: 'tag',
    },
  ]

  if (initiative.amenities && initiative.amenities.length > 0) {
    featuresListItems.push({
      label: translations.overviewFeaturesKeys.amenities,
      content:
        initiative.amenities?.map((amenity) => amenity.name).join(', ') ||
        notSpecified,
      styling: 'multi-line-text',
    })
  }

  return (
    <Column gap={isMobile ? '2rem' : '2.5rem'} alignItems="stretch">
      <ParagraphCollapsible>{initiative.vision}</ParagraphCollapsible>

      {renderSearchingDescription && (
        <Column gap={0}>
          <Title.H4>{translations.overviewTab.whoDoWeLookFor}</Title.H4>
          <ContentCollapsible truncateAfter={5}>
            <ParagraphedText>
              {initiative.searchingForMembersDescription}
            </ParagraphedText>
          </ContentCollapsible>
        </Column>
      )}

      {pageContentBlocks.map((block, index) => (
        <ContentSection
          key={block.id ?? index}
          section={{
            type: 'text',
            title: block.title,
            body: block.body,
            truncationThreshold: 5,
          }}
        />
      ))}

      {renderMap && (
        <Column alignItems="stretch">
          <ContentSection
            section={{
              type: 'location',
              title: translations.overviewTab.searchArea,
              subtitle: address,
              lat: geo.lat,
              long: geo.long,
              marker: 'initiative',
            }}
          />
        </Column>
      )}

      <ContentSection
        section={{
          type: 'data',
          title: translations.overviewTab.features,
          listItems: featuresListItems.map((item) => ({
            label: item.label,
            content: item.content,
            styling: item.styling,
          })),
        }}
      />
      <ContentSection
        section={{
          type: 'data',
          title: translations.overviewTab.contact,
          listItems: contactInformationListItems.map((item) => ({
            label: item.label,
            content: item.content,
            styling: item.styling,
          })),
        }}
      />
    </Column>
  )
}
